import React, { useState, useEffect } from 'react';
import HeaderOne from "../components/HeaderTwo";
import BottomFooter from "../components/BottomFooter";
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const palette = {
    lightMint: "#C3DCD8",
    mutedTeal: "#93B0AC",
    softGrey: "#DBD9DA",
    mutedGold: "#C5BD96",
    bgWhite: "#FFFFFF",
    bgIce: "#F8FBFB"
};

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // CMS data state
    const [contactItems, setContactItems] = useState([]);
    const [company, setCompany] = useState(null);
    const [cmsLoading, setCmsLoading] = useState(true);

    // Expanded sections state
    const [expandedSections, setExpandedSections] = useState({
        contact_form: true,
        contact_info: true,
        map_section: true
    });

    // Fetch CMS data on component mount
    useEffect(() => {
        window.scrollTo(0, 0);
        fetchContactPageData();
    }, []);

    const fetchContactPageData = async () => {
        try {
            const response = await axios.get(`${API_URL}/contact-page/page_data/`);
            if (response.data.success) {
                setContactItems(response.data.contact_items);
                setCompany(response.data.company);
            }
        } catch (error) {
            console.error('Failed to fetch contact page data:', error);
        } finally {
            setCmsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (successMessage) setSuccessMessage('');
        if (errorMessage) setErrorMessage('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const response = await axios.post(`${API_URL}/contact/`, {
                full_name: formData.name,
                email: formData.email,
                phone_number: formData.phone,
                subject: formData.subject,
                message: formData.message
            });

            setSuccessMessage(response.data.message || 'Thank you for contacting us! We will get back to you soon.');

            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });

            setTimeout(() => {
                setSuccessMessage('');
            }, 5000);

        } catch (error) {
            console.error('Contact form error:', error);

            if (error.response?.data) {
                const errors = error.response.data;
                let errorMsg = 'Please fix the following errors:\n';

                Object.keys(errors).forEach(key => {
                    if (Array.isArray(errors[key])) {
                        errorMsg += `- ${key}: ${errors[key].join(', ')}\n`;
                    } else {
                        errorMsg += `- ${key}: ${errors[key]}\n`;
                    }
                });

                setErrorMessage(errorMsg);
            } else {
                setErrorMessage('Failed to send message. Please check your internet connection and try again.');
            }

            setTimeout(() => {
                setErrorMessage('');
            }, 8000);
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const toggleAllSections = (expand) => {
        setExpandedSections({
            contact_form: expand,
            contact_info: expand,
            map_section: expand
        });
    };

    // Helper function to render line breaks
    const renderWithLineBreaks = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, index) => (
            <React.Fragment key={index}>
                {line}
                {index < text.split('\n').length - 1 && <br />}
            </React.Fragment>
        ));
    };

    return (
        <>
            <HeaderOne />

            <div className="contact-container" style={{ fontFamily: "'Inter', sans-serif", backgroundColor: palette.bgWhite }}>
                {/* Hero Section */}
                <section
                    className="contact-hero-section"
                    style={{
                        background: "linear-gradient(135deg, #1D2A3A 0%, #2C3E50 100%)",
                        padding: "80px 20px",
                        textAlign: "center",
                        position: "relative",
                        overflow: "hidden",
                        marginBottom: "40px"
                    }}
                >
                    <div className="hero-glow" style={{
                        position: "absolute",
                        top: "-50%",
                        left: "-20%",
                        width: "140%",
                        height: "200%",
                        background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                        pointerEvents: "none"
                    }}></div>

                    <div
                        className="hero-content"
                        style={{ maxWidth: "1000px", margin: "0 auto", position: "relative", zIndex: 1 }}
                    >
                        <h1
                            className="hero-title"
                            style={{
                                fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
                                fontWeight: "800",
                                marginBottom: "20px",
                                color: "white",
                                textShadow: "0 2px 10px rgba(0,0,0,0.1)"
                            }}
                        >
                            Contact Us
                        </h1>
                        <p
                            className="hero-subtitle"
                            style={{
                                fontSize: "1.25rem",
                                color: "white",
                                opacity: "0.95",
                                lineHeight: "1.6",
                                maxWidth: "700px",
                                margin: "0 auto"
                            }}
                        >
                            Get in touch with us today. We're here to help!
                        </p>
                    </div>
                </section>

                {/* Main Content */}
                <section className="contact-main-content">
                    <div
                        className="content-wrapper"
                        style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}
                    >
                        {/* Header and Control Buttons Section */}
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "40px",
                            flexWrap: "wrap",
                            gap: "20px"
                        }}>
                            <h2
                                className="section-title"
                                style={{
                                    color: palette.mutedTeal,
                                    fontSize: "2.2rem",
                                    margin: "0",
                                    fontWeight: "700",
                                    letterSpacing: "-0.5px"
                                }}
                            >
                                Get in Touch
                            </h2>
                            <div style={{ display: "flex", gap: "12px" }}>
                                <button
                                    onClick={() => toggleAllSections(true)}
                                    style={{
                                        backgroundColor: palette.lightMint,
                                        color: palette.mutedTeal,
                                        border: "none",
                                        padding: "10px 24px",
                                        borderRadius: "30px",
                                        cursor: "pointer",
                                        fontWeight: "600",
                                        fontSize: "0.9rem",
                                        transition: "all 0.3s ease",
                                        boxShadow: "0 2px 8px rgba(195, 220, 216, 0.3)"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(195, 220, 216, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(195, 220, 216, 0.3)";
                                    }}
                                >
                                    Expand All
                                </button>
                                <button
                                    onClick={() => toggleAllSections(false)}
                                    style={{
                                        backgroundColor: palette.bgWhite,
                                        color: palette.mutedTeal,
                                        border: `2px solid ${palette.lightMint}`,
                                        padding: "8px 22px",
                                        borderRadius: "30px",
                                        cursor: "pointer",
                                        fontWeight: "600",
                                        fontSize: "0.9rem",
                                        transition: "all 0.3s ease"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = palette.lightMint;
                                        e.currentTarget.style.color = palette.mutedTeal;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = palette.bgWhite;
                                        e.currentTarget.style.color = palette.mutedTeal;
                                    }}
                                >
                                    Collapse All
                                </button>
                            </div>
                        </div>

                        {/* Contact Form Section */}
                        <section className="contact-form-section" style={{ marginBottom: "60px" }}>

                            <div
                                className="contact-form-card"
                                style={{
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "16px",
                                    marginBottom: "28px",
                                    border: `2px solid ${palette.softGrey}`,
                                    boxShadow: "0 4px 20px rgba(138, 127, 141, 0.06)",
                                    borderTop: `3px solid ${palette.mutedTeal}`,
                                    overflow: "hidden",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer"
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = "0 8px 30px rgba(138, 127, 141, 0.15)";
                                    e.currentTarget.style.borderColor = palette.lightMint;
                                    e.currentTarget.style.borderTopColor = palette.mutedGold;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(138, 127, 141, 0.06)";
                                    e.currentTarget.style.borderColor = palette.softGrey;
                                    e.currentTarget.style.borderTopColor = palette.mutedTeal;
                                }}
                            >
                                <div
                                    className="contact-form-header"
                                    style={{
                                        backgroundColor: palette.bgIce,
                                        padding: "22px 28px",
                                        borderBottom: expandedSections.contact_form ? `2px solid ${palette.lightMint}` : "none",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center"
                                    }}
                                    onClick={() => toggleSection("contact_form")}
                                >
                                    <h3 style={{
                                        color: palette.mutedTeal,
                                        margin: "0",
                                        fontSize: "1.4rem",
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px"
                                    }}>
                                        <span style={{
                                            width: "24px",
                                            height: "24px",
                                            borderRadius: "50%",
                                            backgroundColor: palette.lightMint,
                                            opacity: "0.5",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                            <span style={{
                                                width: "0",
                                                height: "0",
                                                borderLeft: "6px solid transparent",
                                                borderRight: "6px solid transparent",
                                                borderTop: expandedSections.contact_form ? "0" : `6px solid ${palette.mutedTeal}`,
                                                borderBottom: expandedSections.contact_form ? `6px solid ${palette.mutedTeal}` : "0",
                                                transition: "all 0.3s ease"
                                            }}></span>
                                        </span>
                                        Send Us a Message
                                    </h3>
                                    <span style={{
                                        color: palette.mutedTeal,
                                        fontWeight: "600",
                                        fontSize: "14px"
                                    }}>
                                        {expandedSections.contact_form ? "Click to collapse" : "Click to expand"}
                                    </span>
                                </div>

                                <div
                                    className="contact-form-content"
                                    style={{
                                        padding: expandedSections.contact_form ? "28px" : "0",
                                        backgroundColor: "#FCFCFC",
                                        maxHeight: expandedSections.contact_form ? "5000px" : "0",
                                        opacity: expandedSections.contact_form ? "1" : "0",
                                        overflow: "hidden",
                                        transition: "all 0.5s ease"
                                    }}
                                >
                                    <p style={{
                                        color: "#7A6F8B",
                                        marginBottom: "24px",
                                        lineHeight: "1.8"
                                    }}>
                                        Have a question or ready to get started? Fill out the form below and we'll get back to you as soon as possible.
                                    </p>

                                    {/* Success Message */}
                                    {successMessage && (
                                        <div style={{
                                            padding: '15px',
                                            marginBottom: '20px',
                                            backgroundColor: '#d4edda',
                                            border: '1px solid #c3e6cb',
                                            borderRadius: '8px',
                                            color: '#155724',
                                            animation: 'slideDown 0.3s ease-in-out'
                                        }}>
                                            <strong>✅ Success!</strong> {successMessage}
                                        </div>
                                    )}

                                    {/* Error Message */}
                                    {errorMessage && (
                                        <div style={{
                                            padding: '15px',
                                            marginBottom: '20px',
                                            backgroundColor: '#f8d7da',
                                            border: '1px solid #f5c6cb',
                                            borderRadius: '8px',
                                            color: '#721c24',
                                            whiteSpace: 'pre-line',
                                            animation: 'slideDown 0.3s ease-in-out'
                                        }}>
                                            <strong>❌ Error!</strong> {errorMessage}
                                        </div>
                                    )}

                                    <form onSubmit={handleSubmit}>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                            gap: '20px',
                                            marginBottom: '20px'
                                        }}>
                                            <div>
                                                <label htmlFor="name" style={{
                                                    display: 'block',
                                                    marginBottom: '8px',
                                                    color: '#7A6F8B',
                                                    fontWeight: '500',
                                                    fontSize: '0.95rem'
                                                }}>
                                                    Full Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    placeholder="Your Full Name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: `2px solid ${palette.softGrey}`,
                                                        borderRadius: '8px',
                                                        fontSize: '1rem',
                                                        color: '#7A6F8B',
                                                        backgroundColor: loading ? '#f5f5f5' : 'white',
                                                        transition: 'all 0.3s ease',
                                                        outline: 'none'
                                                    }}
                                                    onFocus={(e) => e.currentTarget.style.borderColor = palette.mutedTeal}
                                                    onBlur={(e) => e.currentTarget.style.borderColor = palette.softGrey}
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="email" style={{
                                                    display: 'block',
                                                    marginBottom: '8px',
                                                    color: '#7A6F8B',
                                                    fontWeight: '500',
                                                    fontSize: '0.95rem'
                                                }}>
                                                    Email Address *
                                                </label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    placeholder="Your Email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: `2px solid ${palette.softGrey}`,
                                                        borderRadius: '8px',
                                                        fontSize: '1rem',
                                                        color: '#7A6F8B',
                                                        backgroundColor: loading ? '#f5f5f5' : 'white',
                                                        transition: 'all 0.3s ease',
                                                        outline: 'none'
                                                    }}
                                                    onFocus={(e) => e.currentTarget.style.borderColor = palette.mutedTeal}
                                                    onBlur={(e) => e.currentTarget.style.borderColor = palette.softGrey}
                                                />
                                            </div>
                                        </div>

                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                            gap: '20px',
                                            marginBottom: '20px'
                                        }}>
                                            <div>
                                                <label htmlFor="phone" style={{
                                                    display: 'block',
                                                    marginBottom: '8px',
                                                    color: '#7A6F8B',
                                                    fontWeight: '500',
                                                    fontSize: '0.95rem'
                                                }}>
                                                    Phone Number *
                                                </label>
                                                <input
                                                    type="tel"
                                                    id="phone"
                                                    name="phone"
                                                    placeholder="(902) 555-1234"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: `2px solid ${palette.softGrey}`,
                                                        borderRadius: '8px',
                                                        fontSize: '1rem',
                                                        color: '#7A6F8B',
                                                        backgroundColor: loading ? '#f5f5f5' : 'white',
                                                        transition: 'all 0.3s ease',
                                                        outline: 'none'
                                                    }}
                                                    onFocus={(e) => e.currentTarget.style.borderColor = palette.mutedTeal}
                                                    onBlur={(e) => e.currentTarget.style.borderColor = palette.softGrey}
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="subject" style={{
                                                    display: 'block',
                                                    marginBottom: '8px',
                                                    color: '#7A6F8B',
                                                    fontWeight: '500',
                                                    fontSize: '0.95rem'
                                                }}>
                                                    Subject *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="subject"
                                                    name="subject"
                                                    placeholder="What is this about?"
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loading}
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px 16px',
                                                        border: `2px solid ${palette.softGrey}`,
                                                        borderRadius: '8px',
                                                        fontSize: '1rem',
                                                        color: '#7A6F8B',
                                                        backgroundColor: loading ? '#f5f5f5' : 'white',
                                                        transition: 'all 0.3s ease',
                                                        outline: 'none'
                                                    }}
                                                    onFocus={(e) => e.currentTarget.style.borderColor = palette.mutedTeal}
                                                    onBlur={(e) => e.currentTarget.style.borderColor = palette.softGrey}
                                                />
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '24px' }}>
                                            <label htmlFor="message" style={{
                                                display: 'block',
                                                marginBottom: '8px',
                                                color: '#7A6F8B',
                                                fontWeight: '500',
                                                fontSize: '0.95rem'
                                            }}>
                                                Message *
                                            </label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                placeholder="Please tell us more about your request..."
                                                rows="6"
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                disabled={loading}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    border: `2px solid ${palette.softGrey}`,
                                                    borderRadius: '8px',
                                                    fontSize: '1rem',
                                                    color: '#7A6F8B',
                                                    backgroundColor: loading ? '#f5f5f5' : 'white',
                                                    transition: 'all 0.3s ease',
                                                    outline: 'none',
                                                    fontFamily: 'inherit',
                                                    resize: 'vertical'
                                                }}
                                                onFocus={(e) => e.currentTarget.style.borderColor = palette.mutedTeal}
                                                onBlur={(e) => e.currentTarget.style.borderColor = palette.softGrey}
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            style={{
                                                width: '100%',
                                                padding: '14px 28px',
                                                backgroundColor: loading ? palette.softGrey : palette.mutedTeal,
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '30px',
                                                fontSize: '1.1rem',
                                                fontWeight: '600',
                                                cursor: loading ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.3s ease',
                                                boxShadow: `0 4px 12px ${palette.lightMint}60`
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!loading) {
                                                    e.currentTarget.style.backgroundColor = palette.lightMint;
                                                    e.currentTarget.style.color = palette.mutedTeal;
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = `0 6px 20px ${palette.lightMint}80`;
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!loading) {
                                                    e.currentTarget.style.backgroundColor = palette.mutedTeal;
                                                    e.currentTarget.style.color = 'white';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = `0 4px 12px ${palette.lightMint}60`;
                                                }
                                            }}
                                        >
                                            {loading ? 'Sending...' : 'Send Message'}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </section>

                        {/* Contact Information Section */}
                        <section className="contact-info-section" style={{ marginBottom: "40px" }}>
                            <h2
                                className="section-title"
                                style={{
                                    color: "#7A6F8B",
                                    fontSize: "2.2rem",
                                    marginBottom: "40px",
                                    paddingBottom: "15px",
                                    borderBottom: "3px solid #D4E2D4",
                                    fontWeight: "300",
                                    letterSpacing: "1px"
                                }}
                            >
                                Contact Information
                            </h2>

                            {cmsLoading ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '40px 0',
                                    color: '#8A7F8D'
                                }}>
                                    <p>Loading contact information...</p>
                                </div>
                            ) : (
                                <div
                                    className="contact-info-card"
                                    style={{
                                        backgroundColor: palette.bgWhite,
                                        borderRadius: "16px",
                                        marginBottom: "28px",
                                        border: `2px solid ${palette.softGrey}`,
                                        boxShadow: "0 4px 20px rgba(138, 127, 141, 0.06)",
                                        borderTop: `3px solid ${palette.mutedGold}`,
                                        overflow: "hidden",
                                        transition: "all 0.3s ease",
                                        cursor: "pointer"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = "0 8px 30px rgba(138, 127, 141, 0.15)";
                                        e.currentTarget.style.borderColor = palette.lightMint;
                                        e.currentTarget.style.borderTopColor = palette.mutedTeal;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = "0 4px 20px rgba(138, 127, 141, 0.06)";
                                        e.currentTarget.style.borderColor = palette.softGrey;
                                        e.currentTarget.style.borderTopColor = palette.mutedGold;
                                    }}
                                >
                                    <div
                                        className="contact-info-header"
                                        style={{
                                            backgroundColor: palette.bgIce,
                                            padding: "22px 28px",
                                            borderBottom: expandedSections.contact_info ? `2px solid ${palette.lightMint}` : "none",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center"
                                        }}
                                        onClick={() => toggleSection("contact_info")}
                                    >
                                        <h3 style={{
                                            color: palette.mutedTeal,
                                            margin: "0",
                                            fontSize: "1.4rem",
                                            fontWeight: "600",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px"
                                        }}>
                                            <span style={{
                                                width: "24px",
                                                height: "24px",
                                                borderRadius: "50%",
                                                backgroundColor: palette.lightMint,
                                                opacity: "0.5",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}>
                                                <span style={{
                                                    width: "0",
                                                    height: "0",
                                                    borderLeft: "6px solid transparent",
                                                    borderRight: "6px solid transparent",
                                                    borderTop: expandedSections.contact_info ? "0" : `6px solid ${palette.mutedTeal}`,
                                                    borderBottom: expandedSections.contact_info ? `6px solid ${palette.mutedTeal}` : "0",
                                                    transition: "all 0.3s ease"
                                                }}></span>
                                            </span>
                                            Our Contact Details
                                        </h3>
                                        <span style={{
                                            color: palette.mutedTeal,
                                            fontWeight: "600",
                                            fontSize: "14px"
                                        }}>
                                            {expandedSections.contact_info ? "Click to collapse" : "Click to expand"}
                                        </span>
                                    </div>

                                    <div
                                        className="contact-info-content"
                                        style={{
                                            padding: expandedSections.contact_info ? "28px" : "0",
                                            backgroundColor: "#FCFCFC",
                                            maxHeight: expandedSections.contact_info ? "5000px" : "0",
                                            opacity: expandedSections.contact_info ? "1" : "0",
                                            overflow: "hidden",
                                            transition: "all 0.5s ease"
                                        }}
                                    >
                                        {/* Dynamic Contact Items */}
                                        <div style={{
                                            display: 'grid',
                                            gap: '24px',
                                            marginBottom: contactItems.length > 0 ? '32px' : '0'
                                        }}>
                                            {contactItems.map((item) => (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        padding: '20px',
                                                        backgroundColor: palette.bgIce,
                                                        borderRadius: '12px',
                                                        border: `1px solid ${palette.softGrey}`,
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#FFFFFF';
                                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(138, 127, 141, 0.1)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = '#F8F9FA';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }}
                                                >
                                                    <h4 style={{
                                                        color: '#7A6F8B',
                                                        fontSize: '1.1rem',
                                                        fontWeight: '600',
                                                        marginBottom: '12px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px'
                                                    }}>
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '40px',
                                                                height: '40px',
                                                                borderRadius: '50%',
                                                                backgroundColor: palette.lightMint,
                                                                border: `1px solid ${palette.mutedTeal}`,
                                                                color: palette.mutedTeal,
                                                                fontSize: '1.3rem'
                                                            }}
                                                        >
                                                            {item.icon}
                                                        </span>
                                                        {item.title}
                                                    </h4>
                                                    <p style={{
                                                        color: '#7A6F8B',
                                                        lineHeight: '1.8',
                                                        margin: '0 0 8px 52px',
                                                        fontSize: '0.95rem'
                                                    }}>
                                                        {renderWithLineBreaks(item.content)}
                                                    </p>
                                                    {item.subcontent && (
                                                        <p style={{
                                                            color: '#8A7F8D',
                                                            lineHeight: '1.6',
                                                            margin: '0 0 0 52px',
                                                            fontSize: '0.9rem',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            {renderWithLineBreaks(item.subcontent)}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Dynamic Social Media Links */}
                                        {company && company.social_media && company.social_media.length > 0 && (
                                            <div style={{
                                                padding: '24px',
                                                backgroundColor: palette.bgIce,
                                                borderRadius: '12px',
                                                border: `1px solid ${palette.softGrey}`,
                                                textAlign: 'center'
                                            }}>
                                                <h4 style={{
                                                    color: palette.mutedTeal,
                                                    fontSize: '1.2rem',
                                                    fontWeight: '600',
                                                    marginBottom: '20px'
                                                }}>
                                                    Follow Us
                                                </h4>
                                                <div style={{
                                                    display: 'flex',
                                                    gap: '15px',
                                                    justifyContent: 'center',
                                                    flexWrap: 'wrap'
                                                }}>
                                                    {company.social_media.map((social, index) => {
                                                        const getSocialConfig = (platform) => {
                                                            const configs = {
                                                                'Facebook': { icon: 'fab fa-facebook-f', color: '#3b5998' },
                                                                'Instagram': { icon: 'fab fa-instagram', color: '#ac2bac' },
                                                                'Twitter': { icon: 'fab fa-twitter', color: '#55acee' },
                                                                'LinkedIn': { icon: 'fab fa-linkedin-in', color: '#0082ca' },
                                                                'YouTube': { icon: 'fab fa-youtube', color: '#ed302f' },
                                                                'Pinterest': { icon: 'fab fa-pinterest', color: '#c61118' },
                                                                'TikTok': { icon: 'fab fa-tiktok', color: '#000000' },
                                                                'WhatsApp': { icon: 'fab fa-whatsapp', color: '#25d366' },
                                                                'Telegram': { icon: 'fab fa-telegram', color: '#0088cc' },
                                                                'Reddit': { icon: 'fab fa-reddit-alien', color: '#ff4500' },
                                                                'Snapchat': { icon: 'fab fa-snapchat-ghost', color: '#fffc00' },
                                                                'GitHub': { icon: 'fab fa-github', color: '#333333' },
                                                                'Dribbble': { icon: 'fab fa-dribbble', color: '#ec4a89' },
                                                                'Slack': { icon: 'fab fa-slack-hash', color: '#481449' },
                                                                'Vimeo': { icon: 'fab fa-vimeo-v', color: '#1ab7ea' }
                                                            };
                                                            return configs[platform] || { icon: 'fas fa-link', color: '#17a2b8' };
                                                        };

                                                        const config = getSocialConfig(social.platform);

                                                        return (
                                                            <a
                                                                key={index}
                                                                href={social.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={social.platform}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: '#ffffff',
                                                                    border: `2px solid ${config.color}`,
                                                                    textDecoration: 'none',
                                                                    transition: 'all 0.3s ease',
                                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.transform = 'translateY(-3px) scale(1.1)';
                                                                    e.currentTarget.style.backgroundColor = config.color;
                                                                    e.currentTarget.style.boxShadow = `0 6px 15px ${config.color}40`;
                                                                    const icon = e.currentTarget.querySelector('i');
                                                                    if (icon) icon.style.color = '#ffffff';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                                                    const icon = e.currentTarget.querySelector('i');
                                                                    if (icon) icon.style.color = config.color;
                                                                }}
                                                            >
                                                                <i
                                                                    className={config.icon}
                                                                    style={{
                                                                        fontSize: '1.5rem',
                                                                        color: config.color,
                                                                        transition: 'color 0.3s ease'
                                                                    }}
                                                                ></i>
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </section>

                        {/* Dynamic Map Section */}
                        {company && company.map_link && (
                            <section className="map-section" style={{ marginBottom: "40px" }}>
                                <h2
                                    className="section-title"
                                    style={{
                                        color: palette.mutedTeal,
                                        fontSize: "2.2rem",
                                        marginBottom: "40px",
                                        fontWeight: "700",
                                        letterSpacing: "-0.5px"
                                    }}
                                >
                                    Our Location
                                </h2>

                                <div
                                    className="map-card"
                                    style={{
                                        backgroundColor: palette.bgWhite,
                                        borderRadius: "16px",
                                        marginBottom: "28px",
                                        border: `2px solid ${palette.softGrey}`,
                                        boxShadow: "0 4px 20px rgba(138, 127, 141, 0.06)",
                                        borderTop: `3px solid ${palette.lightMint}`,
                                        overflow: "hidden",
                                        transition: "all 0.3s ease",
                                        cursor: "pointer"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = "0 8px 30px rgba(138, 127, 141, 0.15)";
                                        e.currentTarget.style.borderColor = palette.lightMint;
                                        e.currentTarget.style.borderTopColor = palette.mutedTeal;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = "0 4px 20px rgba(138, 127, 141, 0.06)";
                                        e.currentTarget.style.borderColor = palette.softGrey;
                                        e.currentTarget.style.borderTopColor = palette.lightMint;
                                    }}
                                >
                                    <div
                                        className="map-header"
                                        style={{
                                            backgroundColor: palette.bgIce,
                                            padding: "22px 28px",
                                            borderBottom: expandedSections.map_section ? `2px solid ${palette.lightMint}` : "none",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center"
                                        }}
                                        onClick={() => toggleSection("map_section")}
                                    >
                                        <h3 style={{
                                            color: palette.mutedTeal,
                                            margin: "0",
                                            fontSize: "1.4rem",
                                            fontWeight: "600",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px"
                                        }}>
                                            <span style={{
                                                width: "24px",
                                                height: "24px",
                                                borderRadius: "50%",
                                                backgroundColor: palette.lightMint,
                                                opacity: "0.5",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}>
                                                <span style={{
                                                    width: "0",
                                                    height: "0",
                                                    borderLeft: "6px solid transparent",
                                                    borderRight: "6px solid transparent",
                                                    borderTop: expandedSections.map_section ? "0" : `6px solid ${palette.mutedTeal}`,
                                                    borderBottom: expandedSections.map_section ? `6px solid ${palette.mutedTeal}` : "0",
                                                    transition: "all 0.3s ease"
                                                }}></span>
                                            </span>
                                            Find Us Here
                                        </h3>
                                        <span style={{
                                            color: palette.mutedTeal,
                                            fontWeight: "600",
                                            fontSize: "14px"
                                        }}>
                                            {expandedSections.map_section ? "Click to collapse" : "Click to expand"}
                                        </span>
                                    </div>

                                    <div
                                        className="map-content"
                                        style={{
                                            padding: expandedSections.map_section ? "0" : "0",
                                            backgroundColor: "#FCFCFC",
                                            maxHeight: expandedSections.map_section ? "5000px" : "0",
                                            opacity: expandedSections.map_section ? "1" : "0",
                                            overflow: "hidden",
                                            transition: "all 0.5s ease"
                                        }}
                                    >
                                        <div
                                            dangerouslySetInnerHTML={{ __html: company.map_link }}
                                            style={{
                                                borderRadius: '0 0 12px 12px',
                                                overflow: 'hidden',
                                                height: '350px'
                                            }}
                                        />
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* Dynamic Footer Section */}
                        {company && company.contact_footer_text && (
                            <div style={{
                                marginTop: '40px',
                                marginBottom: '80px',
                                textAlign: 'center',
                                padding: '24px',
                                backgroundColor: palette.bgIce,
                                borderRadius: '12px',
                                border: `2px solid ${palette.softGrey}`
                            }}>
                                <p style={{
                                    margin: 0,
                                    color: palette.mutedTeal,
                                    fontSize: '1rem',
                                    lineHeight: '1.8'
                                }}>
                                    {company.contact_footer_text}
                                </p>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <BottomFooter />

            <style jsx>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                /* Map iframe styling */
                .map-content iframe {
                    width: 100% !important;
                    height: 350px !important;
                    border: 0 !important;
                }
                
                /* Ensure proper map container */
                .map-content > div {
                    width: 100%;
                }

                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 2rem !important;
                    }
                    
                    .hero-subtitle {
                        font-size: 1rem !important;
                    }
                    
                    .section-title {
                        font-size: 1.8rem !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Contact;